import { ChangeEvent, useEffect, useState } from 'react';
import { OrdersAutoCancelPaymentStatusConfiguration } from '@tiendanube/common/src/domains/orders/orders';
import { OrdersAutoCancelPaymentMethod } from '@tiendanube/common/src/enums';
import { OrdersAutoCancelExpirationTime } from '@tiendanube/common/src/types';
import { CHECKOUT_AVG_EXPIRATION_MINUTES } from '../constants';

function usePaymentMethodsConfiguration(
  configuration: OrdersAutoCancelPaymentStatusConfiguration,
  disabled: boolean,
  onChange: (configuration: OrdersAutoCancelPaymentStatusConfiguration) => void,
  pixPreselectedExpirationTime?: OrdersAutoCancelExpirationTime | null,
  defaultPixExpirationTime?: OrdersAutoCancelExpirationTime | null,
) {
  const [paymentMethodsConfig, setPaymentMethodsConfig] =
    useState<OrdersAutoCancelPaymentStatusConfiguration>(configuration);

  const [selectedSubceedsCheckout, setSelectedSubceedsCheckout] =
    useState(false);

  // Track the original configuration to know when to reset expiration times
  // This should only be set once when the component first receives the configuration
  const [originalConfiguration, setOriginalConfiguration] =
    useState<OrdersAutoCancelPaymentStatusConfiguration | null>(null);

  // Track which payment methods should be focused after enabling toggle
  const [focusedPaymentMethods, setFocusedPaymentMethods] = useState<
    Set<OrdersAutoCancelPaymentMethod>
  >(new Set());

  // Track if PIX expiration time has been manually changed by the user
  const [pixExpirationManuallyChanged, setPixExpirationManuallyChanged] =
    useState<boolean>(false);

  const handleToggleChange = (
    e: ChangeEvent,
    paymentMethod: OrdersAutoCancelPaymentMethod,
    isActive: boolean,
  ): void => {
    if (disabled) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }

    // Check if we're enabling a toggle that was previously disabled
    const wasDisabled = !paymentMethodsConfig[paymentMethod]?.isActive;
    const isBeingEnabled = isActive && wasDisabled;
    const isBeingDisabled = !isActive && !wasDisabled;

    // Determine expiration time to set based on the action
    let expirationTimeToSet =
      paymentMethodsConfig[paymentMethod]?.expirationTime;

    if (isBeingDisabled) {
      // When disabling, check if we should reset expiration time to null
      if (
        originalConfiguration &&
        (originalConfiguration[paymentMethod]?.expirationTime === null ||
          originalConfiguration[paymentMethod]?.expirationTime === undefined)
      ) {
        // Reset to null only if the original configuration had it as null/undefined
        expirationTimeToSet = null;
      }
    } else if (
      isBeingEnabled &&
      paymentMethod === OrdersAutoCancelPaymentMethod.PIX
    ) {
      // When enabling PIX, use preselected expiration time only if user hasn't manually changed it
      if (pixPreselectedExpirationTime && !pixExpirationManuallyChanged) {
        expirationTimeToSet = pixPreselectedExpirationTime;
      } else if (!pixExpirationManuallyChanged && defaultPixExpirationTime) {
        // If no preselected time but we have a default from payment settings, use that
        expirationTimeToSet = defaultPixExpirationTime;
      }
    }

    const newConfig = {
      ...paymentMethodsConfig,
      [paymentMethod]: {
        ...paymentMethodsConfig[paymentMethod],
        isActive,
        expirationTime: expirationTimeToSet,
      },
    };
    setPaymentMethodsConfig(newConfig);
    onChange(newConfig);

    // If toggle is being enabled (from disabled to enabled), mark for focus
    if (isBeingEnabled) {
      setFocusedPaymentMethods((prev) => new Set(prev).add(paymentMethod));
      // Clear focus state after the focus has been applied (using the same timeout pattern as useAutoFocus)
      setTimeout(() => {
        setFocusedPaymentMethods((prev) => {
          const newSet = new Set(prev);
          newSet.delete(paymentMethod);
          return newSet;
        });
      });
    }
  };

  const handleExpirationTimeChange = (
    paymentMethod: OrdersAutoCancelPaymentMethod,
    value: number,
  ): void => {
    // Mark PIX expiration as manually changed if user is changing PIX expiration time
    if (paymentMethod === OrdersAutoCancelPaymentMethod.PIX) {
      setPixExpirationManuallyChanged(true);
    }

    const newConfig = {
      ...paymentMethodsConfig,
      [paymentMethod]: {
        ...paymentMethodsConfig[paymentMethod],
        expirationTime: value as OrdersAutoCancelExpirationTime,
      },
    };
    setPaymentMethodsConfig(newConfig);
    setSelectedSubceedsCheckout(
      Object.entries(newConfig).some(
        ([_, config]) =>
          config.isActive &&
          config.expirationTime &&
          config.expirationTime <= CHECKOUT_AVG_EXPIRATION_MINUTES,
      ),
    );
    onChange(newConfig);
  };

  useEffect(() => {
    setPaymentMethodsConfig(configuration);

    // Only set original configuration if it hasn't been set yet
    // This preserves the original state for comparison when toggling
    if (originalConfiguration === null) {
      setOriginalConfiguration(configuration);
    }

    setSelectedSubceedsCheckout(
      Object.entries(configuration).some(
        ([_, config]) =>
          config.isActive &&
          config.expirationTime &&
          config.expirationTime <= CHECKOUT_AVG_EXPIRATION_MINUTES,
      ),
    );
  }, [
    configuration,
    originalConfiguration,
    pixPreselectedExpirationTime,
    defaultPixExpirationTime,
  ]);

  return {
    paymentMethodsConfig,
    handleToggleChange,
    handleExpirationTimeChange,
    focusedPaymentMethods,
    selectedSubceedsCheckout,
  };
}

export default usePaymentMethodsConfiguration;
